import React, { useState } from 'react';
import { Download, Copy, CheckCircle, FileText, Terminal } from 'lucide-react';

export const ConfigGenerationStep: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  const sampleConfig = `{
  "mcpServers": {
    "pipedream-gmail": {
      "command": "npx",
      "args": ["@pipedream/mcp", "gmail"],
      "env": {
        "EXTERNAL_USER_ID": "user123"
      }
    }
  }
}`;

  const handleGenerate = async () => {
    setIsGenerating(true);
    // Simulate config generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsGenerated(true);
    setIsGenerating(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(sampleConfig);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isGenerated) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Configuration Generated!
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Your MCP configuration is ready for Claude Desktop
          </p>
        </div>

        {/* Generated Config */}
        <div className="space-y-4">
          <div className="bg-gray-900 dark:bg-gray-800 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 bg-gray-800 dark:bg-gray-700">
              <span className="text-sm text-gray-300">claude_desktop_config.json</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 dark:bg-gray-600 text-gray-300 rounded hover:bg-gray-600 dark:hover:bg-gray-500"
              >
                {copied ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="p-4 text-sm text-gray-300 overflow-x-auto">
              <code>{sampleConfig}</code>
            </pre>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">
            Installation Instructions:
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                1
              </div>
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white">
                  Locate Claude Desktop Config
                </h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Find your Claude Desktop configuration file:
                </p>
                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                  ~/Library/Application Support/Claude/claude_desktop_config.json
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                2
              </div>
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white">
                  Update Configuration
                </h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Copy the generated config and paste it into your Claude Desktop configuration file.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                3
              </div>
              <div>
                <h5 className="font-medium text-gray-900 dark:text-white">
                  Restart Claude Desktop
                </h5>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Close and reopen Claude Desktop to load the new configuration.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Download Option */}
        <div className="text-center">
          <button
            onClick={() => {
              const blob = new Blob([sampleConfig], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'claude_desktop_config.json';
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download Config File
          </button>
        </div>

        {/* Success Tips */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            🎉 You're almost done!
          </h4>
          <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
            <li>• Your MCP configuration is ready to use</li>
            <li>• After restarting Claude Desktop, you can use your connected apps</li>
            <li>• Try asking "Check my latest emails" or "Send a message to Slack"</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          Generate MCP Configuration
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Create a configuration file for Claude Desktop with your connected apps
        </p>
      </div>

      {/* What is MCP */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
          What is MCP?
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          Model Context Protocol (MCP) is an open standard that allows AI assistants like Claude to securely connect to external tools and data sources.
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Secure and standardized way to extend AI capabilities</li>
          <li>• Works directly with Claude Desktop</li>
          <li>• No additional software required</li>
        </ul>
      </div>

      {/* Configuration Preview */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900 dark:text-white">
          Your configuration will include:
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Terminal className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
            <h5 className="font-medium text-gray-900 dark:text-white mb-1">
              Server Configuration
            </h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Connection details for your selected apps and services
            </p>
          </div>

          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <FileText className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" />
            <h5 className="font-medium text-gray-900 dark:text-white mb-1">
              Environment Variables
            </h5>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Secure credentials and authentication tokens
            </p>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="text-center">
        {!isGenerating ? (
          <button
            onClick={handleGenerate}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <FileText className="w-5 h-5" />
            Generate Configuration
          </button>
        ) : (
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Generating configuration...</span>
          </div>
        )}
      </div>

      {/* Information */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
          💡 What happens next?
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• We'll generate a configuration file based on your connections</li>
          <li>• You'll get step-by-step installation instructions</li>
          <li>• Your credentials remain secure and encrypted</li>
          <li>• You can modify the configuration anytime</li>
        </ul>
      </div>
    </div>
  );
};